import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Confirm,
  Form,
  Icon,
  Message,
  Modal
} from 'semantic-ui-react';
import styled from 'styled-components';
import {
  LenderInsurance,
  TenantGender,
  updateLenderInsurance
} from '../../../services/lender-insurance';
import { getFilesLinkAxios } from '../../../services/file';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../../services/notification';
import { extractApiErrorMessages } from '../../../utils/apiErrorHandler';
import { maskMoney, maskPhoneNumber, maskZipCode } from '../../../services/masks';
import useDebounce from '../../../services/useDebounce';
import LenderInsurancePropertyWalletSearchDropdown, {
  LenderInsuranceWalletAddressFields,
  WalletTag
} from './LenderInsurancePropertyWalletSearchDropdown';
import { calculateAge } from '../../../utils/calculateAge';
import { genderOptions, maritalStatusOptions } from '../../bond-insurance-budget-form/types';
import { MaritalStatusEnum } from '../../../enums/marital-status.enum';
import {
  SOURCE_TYPE_LABELS,
  STATE_OPTIONS,
  buildFullAddress,
  fetchAddressByCep,
  formatDocument,
  formatMoneyFromNumber,
  formatMoneyInput,
  parseMoneyToNumber,
  parsePropertyAddress,
  toDateInputValue,
  validateDocument
} from './lenderInsuranceForm.helpers';

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1b1c1d;
  line-height: 1.3;

  i.icon {
    margin: 0 !important;
    color: #59249b;
  }
`;

const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const MetaTag = styled.span<{ $tone?: 'neutral' | 'success' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 10px;
  border-radius: 13px;
  background: ${props => (props.$tone === 'success' ? '#e8f5e9' : '#f3f0f7')};
  color: ${props => (props.$tone === 'success' ? '#2e7d32' : '#4a4a4a')};
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  i.icon {
    margin: 0 !important;
    height: auto !important;
    width: auto !important;
    font-size: 12px !important;
    opacity: 0.8;
  }

  &::before {
    ${props =>
      props.$tone === 'success'
        ? `
      content: '';
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background-color: #43a047;
      flex-shrink: 0;
    `
        : ''}
  }
`;

const FormSection = styled.div`
  margin-bottom: 20px;
  padding: 16px 20px 8px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
`;

const SectionTitle = styled.h4`
  color: #59249b;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
`;

const ReadOnlyHint = styled.span`
  display: block;
  margin: -8px 0 12px;
  font-size: 12px;
  color: #888;
`;

type Props = {
  open: boolean;
  insurance: LenderInsurance | null;
  onClose: () => void;
  onSuccess: (updated: LenderInsurance, proposalRegenerated: boolean) => void;
  onViewSource?: (insurance: LenderInsurance) => void;
};

const EditLenderInsuranceModal: React.FC<Props> = ({
  open,
  insurance,
  onClose,
  onSuccess,
  onViewSource
}) => {
  const [loading, setLoading] = useState(false);
  const [hasSignedProposal, setHasSignedProposal] = useState(false);
  const [signedConfirmOpen, setSignedConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const [tenantName, setTenantName] = useState('');
  const [tenantDocument, setTenantDocument] = useState('');
  const [tenantEmail, setTenantEmail] = useState('');
  const [tenantPhone, setTenantPhone] = useState('');
  const [tenantBirthDate, setTenantBirthDate] = useState('');
  const [tenantGender, setTenantGender] = useState<TenantGender | undefined>();
  const [tenantMaritalStatus, setTenantMaritalStatus] = useState<
    MaritalStatusEnum | undefined
  >();
  const [ageError, setAgeError] = useState('');

  const [ownerName, setOwnerName] = useState('');
  const [ownerDocument, setOwnerDocument] = useState('');

  const [propertyZipcode, setPropertyZipcode] = useState('');
  const [propertyStreet, setPropertyStreet] = useState('');
  const [propertyNumber, setPropertyNumber] = useState('');
  const [propertyComplement, setPropertyComplement] = useState('');
  const [propertyNeighborhood, setPropertyNeighborhood] = useState('');
  const [propertyCity, setPropertyCity] = useState('');
  const [propertyState, setPropertyState] = useState('');
  const [addressFallback, setAddressFallback] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [propertyPublicId, setPropertyPublicId] = useState<string | undefined>();
  const [rentValue, setRentValue] = useState('');

  const [initialSnapshot, setInitialSnapshot] = useState('');
  const skipCepFetchRef = useRef(true);
  const addressFallbackRef = useRef(false);
  const formRef = useRef<HTMLDivElement>(null);

  const debouncedCep = useDebounce<string>(propertyZipcode.replace(/\D/g, ''), 500);
  const selectedFromWallet = Boolean(propertyPublicId);
  const isFieldLocked = selectedFromWallet;

  const snapshot = useMemo(
    () =>
      JSON.stringify({
        tenantName,
        tenantDocument,
        tenantEmail,
        tenantPhone,
        tenantBirthDate,
        tenantGender,
        tenantMaritalStatus,
        ownerName,
        ownerDocument,
        propertyZipcode,
        propertyStreet,
        propertyNumber,
        propertyComplement,
        propertyNeighborhood,
        propertyCity,
        propertyState,
        addressFallback,
        propertyPublicId: propertyPublicId || null,
        rentValue
      }),
    [
      tenantName,
      tenantDocument,
      tenantEmail,
      tenantPhone,
      tenantBirthDate,
      tenantGender,
      tenantMaritalStatus,
      ownerName,
      ownerDocument,
      propertyZipcode,
      propertyStreet,
      propertyNumber,
      propertyComplement,
      propertyNeighborhood,
      propertyCity,
      propertyState,
      addressFallback,
      propertyPublicId,
      rentValue
    ]
  );

  const isDirty = Boolean(initialSnapshot) && snapshot !== initialSnapshot;

  useEffect(() => {
    if (!open || !insurance) return;

    skipCepFetchRef.current = true;
    const parsed = parsePropertyAddress(insurance.propertyAddress);
    addressFallbackRef.current = parsed.fallback;
    const nextTenantName = insurance.tenantName || '';
    const nextTenantDocument = formatDocument(insurance.tenantDocument || '');
    const nextTenantEmail = insurance.tenantEmail || '';
    const nextTenantPhone = insurance.tenantPhone
      ? maskPhoneNumber(insurance.tenantPhone)
      : '';
    const nextBirthDate = toDateInputValue(insurance.tenantBirthDate);
    const nextGender = insurance.tenantGender;
    const nextMarital = insurance.tenantMaritalStatus;
    const nextOwnerName = insurance.ownerName || '';
    const nextOwnerDocument = formatDocument(insurance.ownerDocument || '');
    const nextZip = insurance.propertyZipcode
      ? maskZipCode(insurance.propertyZipcode)
      : '';
    const nextRent = formatMoneyFromNumber(insurance.rentValue);

    setTenantName(nextTenantName);
    setTenantDocument(nextTenantDocument);
    setTenantEmail(nextTenantEmail);
    setTenantPhone(nextTenantPhone);
    setTenantBirthDate(nextBirthDate);
    setTenantGender(nextGender);
    setTenantMaritalStatus(nextMarital);
    setAgeError(
      nextBirthDate && calculateAge(nextBirthDate) >= 65
        ? 'Idade limite atingida: Produto válido apenas para clientes com menos de 65 anos.'
        : ''
    );
    setOwnerName(nextOwnerName);
    setOwnerDocument(nextOwnerDocument);
    setPropertyZipcode(nextZip);
    setPropertyStreet(parsed.street);
    setPropertyNumber(parsed.number);
    setPropertyComplement(parsed.complement);
    setPropertyNeighborhood(parsed.neighborhood);
    setAddressFallback(parsed.fallback);
    setPropertyCity(insurance.propertyCity || '');
    setPropertyState(insurance.propertyState || '');
    setPropertyPublicId(insurance.propertyPublicId);
    setRentValue(nextRent);
    setHasSignedProposal(false);
    setSignedConfirmOpen(false);
    setDiscardConfirmOpen(false);
    const enableCepFetch = window.setTimeout(() => {
      skipCepFetchRef.current = false;
    }, 600);

    setInitialSnapshot(
      JSON.stringify({
        tenantName: nextTenantName,
        tenantDocument: nextTenantDocument,
        tenantEmail: nextTenantEmail,
        tenantPhone: nextTenantPhone,
        tenantBirthDate: nextBirthDate,
        tenantGender: nextGender,
        tenantMaritalStatus: nextMarital,
        ownerName: nextOwnerName,
        ownerDocument: nextOwnerDocument,
        propertyZipcode: nextZip,
        propertyStreet: parsed.street,
        propertyNumber: parsed.number,
        propertyComplement: parsed.complement,
        propertyNeighborhood: parsed.neighborhood,
        propertyCity: insurance.propertyCity || '',
        propertyState: insurance.propertyState || '',
        addressFallback: parsed.fallback,
        propertyPublicId: insurance.propertyPublicId || null,
        rentValue: nextRent
      })
    );

    getFilesLinkAxios(insurance.id, 'lender_insurances')
      .then(response => {
        const urls = response?.urls || [];
        setHasSignedProposal(
          urls.some((file: { name?: string }) => file.name === 'LENDER_INSURANCE_SIGNED')
        );
      })
      .catch(() => {
        setHasSignedProposal(false);
      });

    return () => {
      window.clearTimeout(enableCepFetch);
    };
  }, [open, insurance]);

  useEffect(() => {
    if (!open || selectedFromWallet) return;

    const fetchAddress = async () => {
      if (debouncedCep.length !== 8) return;
      if (skipCepFetchRef.current) return;

      setLoadingCep(true);
      try {
        const data = await fetchAddressByCep(debouncedCep);
        const keepStreet = addressFallbackRef.current;
        if (!data) {
          warningNotification(
            'CEP não encontrado',
            'Verifique o CEP informado e tente novamente.'
          );
          if (!keepStreet) {
            setPropertyStreet('');
            setPropertyNeighborhood('');
          }
          setPropertyCity('');
          setPropertyState('');
        } else {
          if (!keepStreet) {
            setPropertyStreet(data.street);
            setPropertyNeighborhood(data.neighborhood);
          }
          setPropertyCity(data.city);
          setPropertyState(data.state);
        }
      } catch (err) {
        warningNotification('Erro ao buscar CEP', 'Não foi possível buscar o endereço.');
      } finally {
        setLoadingCep(false);
      }
    };

    fetchAddress();
  }, [debouncedCep, selectedFromWallet, open]);

  const handleWalletSelect = (
    publicId: string,
    address: LenderInsuranceWalletAddressFields
  ) => {
    setPropertyPublicId(publicId);
    setAddressFallback(false);
    addressFallbackRef.current = false;
    setPropertyZipcode(address.zipcode);
    setPropertyStreet(address.street);
    setPropertyNumber(address.number);
    setPropertyComplement(address.complement);
    setPropertyNeighborhood(address.neighborhood);
    setPropertyCity(address.city);
    setPropertyState(address.state);
  };

  const handleWalletClear = () => {
    setPropertyPublicId(undefined);
  };

  const resolvedAddress = addressFallback
    ? propertyStreet.trim()
    : buildFullAddress(
        propertyStreet,
        propertyNumber,
        propertyComplement,
        propertyNeighborhood
      );

  const willRegenerateProposal = useMemo(() => {
    if (!insurance || !isDirty) return false;
    const currentZip = propertyZipcode.replace(/\D/g, '');
    const originalZip = (insurance.propertyZipcode || '').replace(/\D/g, '');
    return (
      tenantName.trim() !== (insurance.tenantName || '').trim() ||
      tenantDocument.replace(/\D/g, '') !==
        (insurance.tenantDocument || '').replace(/\D/g, '') ||
      tenantPhone.replace(/\D/g, '') !== (insurance.tenantPhone || '').replace(/\D/g, '') ||
      tenantBirthDate !== toDateInputValue(insurance.tenantBirthDate) ||
      tenantGender !== insurance.tenantGender ||
      resolvedAddress !== (insurance.propertyAddress || '') ||
      propertyCity.trim() !== (insurance.propertyCity || '').trim() ||
      propertyState !== (insurance.propertyState || '') ||
      currentZip !== originalZip ||
      parseMoneyToNumber(rentValue) !== Number(insurance.rentValue)
    );
  }, [
    insurance,
    isDirty,
    tenantName,
    tenantDocument,
    tenantPhone,
    tenantBirthDate,
    tenantGender,
    resolvedAddress,
    propertyCity,
    propertyState,
    propertyZipcode,
    rentValue
  ]);

  const isFormValid = () => {
    const hasStructuredAddress =
      propertyStreet.trim() &&
      propertyNumber.trim() &&
      propertyNeighborhood.trim();
    const hasAddress = addressFallback ? propertyStreet.trim() : hasStructuredAddress;

    return (
      tenantName.trim() &&
      tenantDocument.replace(/\D/g, '').length === 11 &&
      tenantBirthDate.trim() !== '' &&
      !!tenantGender &&
      !!tenantMaritalStatus &&
      ownerName.trim() &&
      ownerDocument.replace(/\D/g, '').length >= 11 &&
      propertyZipcode.replace(/\D/g, '').length === 8 &&
      hasAddress &&
      propertyCity.trim() &&
      propertyState.trim() &&
      parseMoneyToNumber(rentValue) > 0 &&
      !ageError
    );
  };

  const focusFirstInvalid = () => {
    const invalid = formRef.current?.querySelector(
      '.error input, .error .dropdown, input:invalid'
    ) as HTMLElement | null;
    invalid?.focus();
  };

  const buildPayload = () => ({
    tenantName: tenantName.trim(),
    tenantDocument: tenantDocument.replace(/\D/g, ''),
    tenantEmail: tenantEmail.trim(),
    tenantPhone: tenantPhone.replace(/\D/g, ''),
    tenantBirthDate,
    tenantGender,
    tenantMaritalStatus,
    ownerName: ownerName.trim(),
    ownerDocument: ownerDocument.replace(/\D/g, ''),
    propertyAddress: resolvedAddress,
    propertyZipcode: propertyZipcode.replace(/\D/g, ''),
    propertyCity: propertyCity.trim(),
    propertyState,
    rentValue: parseMoneyToNumber(rentValue),
    propertyPublicId: propertyPublicId || null,
    walletProperty: {
      street: addressFallback ? undefined : propertyStreet,
      number: addressFallback ? undefined : propertyNumber,
      district: addressFallback ? undefined : propertyNeighborhood,
      complement: addressFallback ? undefined : propertyComplement,
      city: propertyCity,
      state: propertyState,
      zipcode: propertyZipcode.replace(/\D/g, '')
    }
  });

  const submitUpdate = async () => {
    if (!insurance) return;

    if (!isFormValid()) {
      dangerNotification('Erro', 'Preencha os campos obrigatórios.');
      focusFirstInvalid();
      return;
    }

    if (!validateDocument(tenantDocument) || tenantDocument.replace(/\D/g, '').length !== 11) {
      dangerNotification('Erro', 'CPF do locatário inválido. Prestamista é apenas para Pessoa Física.');
      return;
    }

    if (!validateDocument(ownerDocument)) {
      dangerNotification('Erro', 'CPF ou CNPJ do proprietário inválido.');
      return;
    }

    if (tenantBirthDate && calculateAge(tenantBirthDate) >= 65) {
      dangerNotification(
        'Erro',
        'Idade limite atingida: Produto válido apenas para clientes com menos de 65 anos.'
      );
      return;
    }

    setLoading(true);
    try {
      const result = await updateLenderInsurance(insurance.id, buildPayload());
      const { proposalRegenerated, ...updated } = result;
      if (proposalRegenerated) {
        successNotification('Seguro atualizado', 'Proposta de adesão regenerada.');
      } else {
        successNotification('Sucesso', 'Seguro atualizado.');
      }
      onSuccess(updated as LenderInsurance, Boolean(proposalRegenerated));
    } catch (error) {
      dangerNotification(
        'Erro',
        extractApiErrorMessages(error, 'Erro ao atualizar seguro prestamista.')
      );
    } finally {
      setLoading(false);
      setSignedConfirmOpen(false);
    }
  };

  const handleSaveClick = () => {
    if (!isFormValid()) {
      dangerNotification('Erro', 'Preencha os campos obrigatórios.');
      focusFirstInvalid();
      return;
    }
    if (hasSignedProposal && willRegenerateProposal) {
      setSignedConfirmOpen(true);
      return;
    }
    submitUpdate();
  };

  const handleRequestClose = () => {
    if (loading) return;
    if (isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  };

  if (!insurance) return null;

  const sourceLabel = SOURCE_TYPE_LABELS[insurance.sourceType] || insurance.sourceType;
  const isLinkedOrigin = insurance.sourceType !== 'STANDALONE' && Boolean(insurance.sourceId);
  const showNotice = isLinkedOrigin || willRegenerateProposal;

  return (
    <>
      <Modal open={open} onClose={handleRequestClose} size="large">
        <Modal.Header>
          <HeaderTitle>
            <Icon name="pencil" />
            Editar seguro prestamista
          </HeaderTitle>
          <HeaderMeta>
            <MetaTag title={insurance.tenantName}>{insurance.tenantName}</MetaTag>
            {insurance.estate?.name && (
              <MetaTag title={insurance.estate.name}>
                <Icon name="building outline" fitted />
                {insurance.estate.name}
              </MetaTag>
            )}
            <MetaTag $tone="success">Ativo</MetaTag>
            <MetaTag>{sourceLabel}</MetaTag>
          </HeaderMeta>
        </Modal.Header>

        <Modal.Content scrolling>
          <div ref={formRef}>
            {showNotice && (
              <Message info size="small">
                {isLinkedOrigin && (
                  <div>
                    Não atualiza o {sourceLabel.toLowerCase()} de origem.{' '}
                    {onViewSource && (
                      <Button
                        type="button"
                        size="mini"
                        compact
                        basic
                        onClick={() => onViewSource(insurance)}
                      >
                        Ver origem
                        <Icon name="arrow right" />
                      </Button>
                    )}
                  </div>
                )}
                {willRegenerateProposal && !hasSignedProposal && (
                  <p>Adesão HDI será regenerada.</p>
                )}
                {willRegenerateProposal && hasSignedProposal && (
                  <p>
                    Proposta assinada existe. Salvar regenera a adesão HDI e deixa a
                    assinada desatualizada.
                  </p>
                )}
              </Message>
            )}

            <Form>
              <FormSection>
                <SectionTitle>
                  <Icon name="user" /> Locatário
                </SectionTitle>
                <Form.Group widths="equal">
                  <Form.Input
                    label="Nome *"
                    placeholder="Nome completo"
                    value={tenantName}
                    onChange={e => setTenantName(e.target.value)}
                  />
                  <Form.Input
                    label="CPF *"
                    placeholder="000.000.000-00"
                    value={tenantDocument}
                    onChange={e => setTenantDocument(formatDocument(e.target.value))}
                    maxLength={14}
                  />
                </Form.Group>
                <Form.Group widths="equal">
                  <Form.Input
                    label="E-mail"
                    placeholder="email@exemplo.com"
                    type="email"
                    value={tenantEmail}
                    onChange={e => setTenantEmail(e.target.value)}
                  />
                  <Form.Input
                    label="Telefone"
                    placeholder="(00) 00000-0000"
                    value={tenantPhone}
                    onChange={e => setTenantPhone(maskPhoneNumber(e.target.value))}
                    maxLength={15}
                  />
                </Form.Group>
                <Form.Group widths="equal">
                  <Form.Input
                    label="Data de Nascimento *"
                    type="date"
                    value={tenantBirthDate}
                    onChange={e => {
                      const value = e.target.value;
                      setTenantBirthDate(value);
                      if (value && calculateAge(value) >= 65) {
                        setAgeError(
                          'Idade limite atingida: Produto válido apenas para clientes com menos de 65 anos.'
                        );
                      } else {
                        setAgeError('');
                      }
                    }}
                    error={ageError ? { content: ageError } : false}
                  />
                  <Form.Select
                    label="Sexo *"
                    placeholder="Selecione"
                    options={genderOptions}
                    value={tenantGender}
                    onChange={(_, { value }) => setTenantGender(value as TenantGender)}
                  />
                  <Form.Select
                    label="Estado Civil *"
                    placeholder="Selecione"
                    options={maritalStatusOptions}
                    value={tenantMaritalStatus}
                    onChange={(_, { value }) =>
                      setTenantMaritalStatus(value as MaritalStatusEnum)
                    }
                  />
                </Form.Group>
              </FormSection>

              <FormSection>
                <SectionTitle>
                  <Icon name="building" /> Proprietário
                </SectionTitle>
                <Form.Group widths="equal">
                  <Form.Input
                    label="Nome *"
                    placeholder="Nome completo"
                    value={ownerName}
                    onChange={e => setOwnerName(e.target.value)}
                  />
                  <Form.Input
                    label="CPF/CNPJ *"
                    placeholder="000.000.000-00"
                    value={ownerDocument}
                    onChange={e => setOwnerDocument(formatDocument(e.target.value))}
                    maxLength={18}
                  />
                </Form.Group>
              </FormSection>

              <FormSection>
                <SectionTitle>
                  <Icon name="home" /> Imóvel
                </SectionTitle>
                <LenderInsurancePropertyWalletSearchDropdown
                  estateId={insurance.estateId}
                  propertyPublicId={propertyPublicId}
                  onSelect={handleWalletSelect}
                  onClear={handleWalletClear}
                />
                {selectedFromWallet && (
                  <WalletTag>
                    Imóvel da carteira — desvincule para corrigir o endereço
                  </WalletTag>
                )}
                <Form.Group>
                  <Form.Input
                    width={4}
                    label="CEP *"
                    placeholder="00000-000"
                    value={propertyZipcode}
                    onChange={e => setPropertyZipcode(maskZipCode(e.target.value))}
                    maxLength={9}
                    loading={loadingCep}
                    icon={loadingCep ? undefined : 'search'}
                    disabled={isFieldLocked}
                  />
                  {addressFallback ? (
                    <Form.Input
                      width={12}
                      label="Endereço *"
                      placeholder="Endereço completo"
                      value={propertyStreet}
                      onChange={e => setPropertyStreet(e.target.value)}
                      disabled={isFieldLocked}
                    />
                  ) : (
                    <>
                      <Form.Input
                        width={9}
                        label="Rua/Logradouro *"
                        placeholder="Nome da rua"
                        value={propertyStreet}
                        onChange={e => setPropertyStreet(e.target.value)}
                        loading={loadingCep}
                        disabled={loadingCep || isFieldLocked}
                      />
                      <Form.Input
                        width={3}
                        label="Número *"
                        placeholder="Nº"
                        value={propertyNumber}
                        onChange={e => setPropertyNumber(e.target.value)}
                        disabled={isFieldLocked}
                      />
                    </>
                  )}
                </Form.Group>
                {addressFallback ? (
                  <Form.Group widths="equal">
                    <Form.Input
                      label="Cidade *"
                      placeholder="Cidade"
                      value={propertyCity}
                      onChange={e => setPropertyCity(e.target.value)}
                      loading={loadingCep}
                      disabled={loadingCep || isFieldLocked}
                    />
                    <Form.Select
                      label="Estado *"
                      placeholder="UF"
                      options={STATE_OPTIONS}
                      value={propertyState}
                      onChange={(_, { value }) => setPropertyState(value as string)}
                      disabled={loadingCep || isFieldLocked}
                      fluid
                    />
                  </Form.Group>
                ) : (
                  <Form.Group>
                    <Form.Input
                      width={4}
                      label="Complemento"
                      placeholder="Apto, Bloco, etc."
                      value={propertyComplement}
                      onChange={e => setPropertyComplement(e.target.value)}
                      disabled={isFieldLocked}
                    />
                    <Form.Input
                      width={4}
                      label="Bairro *"
                      placeholder="Bairro"
                      value={propertyNeighborhood}
                      onChange={e => setPropertyNeighborhood(e.target.value)}
                      loading={loadingCep}
                      disabled={loadingCep || isFieldLocked}
                    />
                    <Form.Input
                      width={5}
                      label="Cidade *"
                      placeholder="Cidade"
                      value={propertyCity}
                      onChange={e => setPropertyCity(e.target.value)}
                      loading={loadingCep}
                      disabled={loadingCep || isFieldLocked}
                    />
                    <Form.Select
                      width={3}
                      label="Estado *"
                      placeholder="UF"
                      options={STATE_OPTIONS}
                      value={propertyState}
                      onChange={(_, { value }) => setPropertyState(value as string)}
                      disabled={loadingCep || isFieldLocked}
                      fluid
                    />
                  </Form.Group>
                )}
              </FormSection>

              <FormSection>
                <SectionTitle>
                  <Icon name="dollar" /> Valores
                </SectionTitle>
                <Form.Group widths="equal">
                  <Form.Input
                    label="Valor do Aluguel *"
                    placeholder="R$ 0,00"
                    value={rentValue}
                    onChange={e => setRentValue(formatMoneyInput(e.target.value))}
                  />
                  <Form.Input
                    label="Valor do Seguro"
                    value={
                      insurance.insuranceValue
                        ? `R$ ${maskMoney(String(insurance.insuranceValue), true)}`
                        : '-'
                    }
                    readOnly
                  />
                </Form.Group>
                <ReadOnlyHint>
                  Taxa congelada na contratação; não recalcula com o aluguel.
                </ReadOnlyHint>
              </FormSection>
            </Form>
          </div>
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={handleRequestClose} disabled={loading}>
            Fechar
          </Button>
          <Button
            color="purple"
            onClick={handleSaveClick}
            loading={loading}
            disabled={!isDirty || !isFormValid() || loading}
          >
            <Icon name="save" /> Salvar
          </Button>
        </Modal.Actions>
      </Modal>

      <Confirm
        open={discardConfirmOpen}
        content="Descartar alterações?"
        cancelButton="Voltar"
        confirmButton="Descartar"
        onCancel={() => setDiscardConfirmOpen(false)}
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
      />

      <Confirm
        open={signedConfirmOpen}
        header="Proposta assinada"
        content="Proposta assinada existe. Salvar regenera a adesão HDI e deixa a assinada desatualizada."
        cancelButton="Voltar"
        confirmButton="Salvar mesmo assim"
        onCancel={() => setSignedConfirmOpen(false)}
        onConfirm={submitUpdate}
      />
    </>
  );
};

export default EditLenderInsuranceModal;
